import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Heading,
  Text,
  Button,
  Card,
  Badge,
  type BadgeProps,
  Spinner,
  Dialog,
  Progress,
  Table,
  Callout,
} from 'frosted-ui';
import {
  Play16,
  Pause16,
  Trophy16,
  ExclamationTriangle16,
  ArrowLeft16,
  EyeSlashed16,
  Eye16,
  InfoCircle16,
  Plus16,
} from '@frosted-ui/icons';
import { api, PlanLimitError } from '../services/api';
import LoadFailed from '../components/LoadFailed';
import ProblemCallout, { problemFrom, type Problem } from '../components/ProblemCallout';
import type { SpotManagement } from '../components/wizard/SpotActions';
import { pathFromPlacements } from '../components/wizard/resumeDraft';
import { useCompanyId } from '../contexts/CompanyContext';
import { useTerms } from '../contexts/TermsContext';
import { useIsMobile } from '../hooks/useIsMobile';
import type {
  Experiment,
  ExperimentMetrics,
  Placement,
  OfferClosedReason,
  PlacementBreakdown,
  VariantMetrics,
  VisitActivity,
} from '../types';
import { formatNumber } from '../utils/format';
import {
  describeLegacyHistory,
  describeOffer,
  formatRpv,
  pluralize,
  variantLabel,
} from '../utils/variants';
import {
  boughtLabel as boughtLabelFor,
  saleWordFor,
  endSummary,
  formatLift,
  liveFor,
  NOT_JUDGED_BY_REVENUE,
  offerWord,
  readSpot,
  trialProgressLine,
  trialTestNote,
  readTest,
  sampleShortfall,
  sampleTarget,
  startedPercent,
} from '../utils/readout';
import {
  archiveEffect,
  placesLabel,
  linksNow,
  spotName,
  unarchiveHint,
  type TestState,
} from '../utils/paths';
import PerformanceTrend from '../components/dashboard/PerformanceTrend';
import UpgradeDialog from '../components/billing/UpgradeDialog';
import { needsProForNewTest } from '../components/billing/plan';
import { usePlan } from '../hooks/usePlan';
import { Rise, Reveal } from '../components/animation';
import PromoteWinnerModal from '../components/dashboard/PromoteWinnerModal';
import ReviewPromptModal from '../components/ReviewPromptModal';
import TermsAcceptanceModal from '../components/TermsAcceptanceModal';
import FirstVisitChecklist, { PlacementLinks } from '../components/wizard/FirstVisitChecklist';

interface ExperimentDashboardProps {
  isReadOnly?: boolean;
  experimentId?: string; // Override for dev mode
}

// Sales we can't tie to any spot (the API's unattributed row).
const NO_SPOT = 'No source recorded';

// More spots than this and the list folds up, so results stay near the top.
const SPOTS_SHOWN_OPEN = 3;

const CLOSED_NEXT_STEP = 'Keep a price and end the test, or archive it.';

const OFFER_CLOSED: Record<OfferClosedReason, { badge: string; body: string }> = {
  sold_out: {
    badge: 'Sold out',
    body: 'This offer reached its sales limit, so buyers see it as sold out. Nothing more will sell.',
  },
  expired: {
    badge: 'Expired',
    body: 'This offer passed its stop date, so buyers see it as no longer available. Nothing more will sell.',
  },
};

function statusBadge(exp: Experiment, closed: OfferClosedReason | null): { label: string; color: BadgeProps['color'] } {
  if (exp.archived) return { label: 'Archived', color: 'gray' };
  if (exp.status === 'winner_declared') return { label: 'Ended', color: 'blue' };
  if (closed) return { label: OFFER_CLOSED[closed].badge, color: 'orange' };
  if (exp.status === 'running') return { label: 'Live', color: 'green' };
  if (exp.status === 'stopped') return { label: 'Paused', color: 'yellow' };
  return { label: 'Draft', color: 'gray' };
}

/** Numbers one variant card shows; "All spots" has more than a single spot. */
interface VariantView {
  variantId: string;
  name: string;
  price: string;
  isControl: boolean;
  rpv: number;
  netRevenue: number;
  visitors: number;
  boughtRate: number | null; // percent: unique starters / unique visitors
  refunds: number | null;
  cancellations: number | null; // recurring offers, all spots: cancels aren't tied to a spot
  rpvVisitors: number; // who the revenue per visitor is over (a trial test: past their trial)
  trialProgress: string | null; // a trial test: where the people who started stand
  probability: number | null; // 0..1
  // A spot filter is on: the model runs on all spots together.
  probabilityNote: 'spot' | null;
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <Text size="1" color="gray" style={{ display: 'block' }}>
        {label}
      </Text>
      <Text size="3" weight="medium" className="vara2-tabular">
        {value}
      </Text>
    </div>
  );
}

function VariantCard({
  view,
  currency,
  earlyRead,
  boughtLabel,
  judgedByRevenue,
  afterTrials,
}: {
  judgedByRevenue: boolean;
  afterTrials: boolean;
  view: VariantView;
  currency: string;
  earlyRead: boolean;
  boughtLabel: string;
}) {
  return (
    <Card size="2" style={{ minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 'var(--space-1) var(--space-3)',
          marginBottom: 'var(--space-3)',
        }}
      >
        <Heading size="4">{view.name}</Heading>
        <Text size="2" color="gray">
          {view.price}
        </Text>
      </div>
      <Text size="1" color="gray" style={{ display: 'block' }}>
        {judgedByRevenue ? (afterTrials ? 'Revenue per visitor, after trials' : 'Revenue per visitor') : boughtLabel}
      </Text>
      <Text
        size="7"
        weight="bold"
        className="vara2-tabular"
        style={{ display: 'block', marginBottom: afterTrials && view.trialProgress ? 0 : 'var(--space-3)' }}
      >
        {judgedByRevenue
          ? view.rpvVisitors === 0
            ? '—'
            : formatRpv(view.rpv, currency)
          : view.boughtRate === null
            ? '—'
            : `${view.boughtRate.toFixed(1)}%`}
      </Text>
      {afterTrials && view.trialProgress && (
        <Text size="1" color="gray" className="vara2-tabular" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
          {view.trialProgress}
        </Text>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${view.cancellations === null ? 3 : 2}, minmax(0, 1fr))`,
          // A label that wraps on a phone doesn't push its number out of line.
          alignItems: 'end',
          gap: 'var(--space-3)',
        }}
      >
        <Stat label="Visitors" value={formatNumber(view.visitors)} />
        {judgedByRevenue ? (
          <Stat
            label={boughtLabel}
            value={view.boughtRate === null ? '—' : `${view.boughtRate.toFixed(1)}%`}
          />
        ) : (
          <Stat label="Collected so far" value={formatRpv(view.netRevenue, currency)} />
        )}
        <Stat label="Refunds" value={view.refunds === null ? '—' : formatNumber(view.refunds)} />
        {view.cancellations !== null && <Stat label="Cancelled" value={formatNumber(view.cancellations)} />}
      </div>
      {judgedByRevenue && !view.isControl && (
      <div
        style={{
          marginTop: 'var(--space-3)',
          paddingTop: 'var(--space-3)',
          borderTop: '1px solid var(--gray-a5)',
        }}
      >
        {view.probabilityNote === 'spot' ? (
          <Text size="2" color="gray">
            Chance it beats Control: shown for the whole test.
          </Text>
        ) : view.probability === null ? (
          <>
            <Text size="1" color="gray" style={{ display: 'block' }}>
              Chance it beats Control
            </Text>
            <Text size="5" weight="bold" className="vara2-tabular">
              —
            </Text>
          </>
        ) : (
          <>
            <Text size="1" color="gray" style={{ display: 'block' }}>
              {earlyRead ? 'Chance it beats Control so far' : 'Chance it beats Control'}
            </Text>
            <Text size="5" weight="bold" className="vara2-tabular">
              {percent(view.probability)}
            </Text>
          </>
        )}
      </div>
      )}
    </Card>
  );
}

export default function ExperimentDashboard({
  isReadOnly = false,
  experimentId,
}: ExperimentDashboardProps) {
  const { id: paramId } = useParams<{ id: string }>();
  const id = experimentId || paramId || '';
  const navigate = useNavigate();
  const companyId = useCompanyId();
  const { isTermsAccepted } = useTerms();
  const isMobile = useIsMobile();
  const { plan } = usePlan(companyId);

  const [experiment, setExperiment] = useState<Experiment | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [metrics, setMetrics] = useState<ExperimentMetrics | null>(null);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [breakdown, setBreakdown] = useState<PlacementBreakdown[]>([]);
  const [activity, setActivity] = useState<VisitActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  // 'pick' once the read is reliable; 'early' for "End test and keep a price".
  const [promoteMode, setPromoteMode] = useState<'pick' | 'early' | null>(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const reviewChecked = useRef(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [actionError, setActionError] = useState<Problem | null>(null);
  // A failed load isn't an empty one: these say which didn't arrive.
  const [metricsFailed, setMetricsFailed] = useState(false);
  const [spotsFailed, setSpotsFailed] = useState(false);
  const [breakdownLoaded, setBreakdownLoaded] = useState(false);
  const [spotsOpen, setSpotsOpen] = useState(false);
  // Go live needs Pro: the prompt is open, and going live is retried once it's active.
  const [showUpgrade, setShowUpgrade] = useState(false);

  const loadExperiment = useCallback(async () => {
    if (!id) return;
    setLoadFailed(false);
    try {
      setExperiment(await api.getExperiment(id));
    } catch (err) {
      console.error('Failed to load test:', err);
      setLoadFailed(true);
    }
  }, [id]);

  const loadAnalytics = useCallback(async () => {
    if (!id || !companyId) return;
    const [m, spots, bd, act] = await Promise.allSettled([
      api.getExperimentMetrics(id, companyId),
      api.getPlacements(id),
      api.getPlacementBreakdown(id),
      api.getVisitActivity(id),
    ]);
    if (m.status === 'fulfilled') setMetrics(m.value);
    setMetricsFailed(m.status === 'rejected');
    if (spots.status === 'fulfilled') setPlacements(spots.value);
    setSpotsFailed(spots.status === 'rejected');
    if (bd.status === 'fulfilled') {
      const rows = bd.value.placements ?? [];
      setBreakdown(rows);
      // The spot being looked at may just have been removed.
      setFilter((current) =>
        current === 'all' || rows.some((r) => (r.placementId ?? 'unattributed') === current) ? current : 'all'
      );
    }
    setBreakdownLoaded(bd.status === 'fulfilled');
    if (act.status === 'fulfilled') setActivity(act.value);
    setLoading(false);
  }, [id, companyId]);

  useEffect(() => {
    loadExperiment();
    loadAnalytics();
  }, [loadExperiment, loadAnalytics]);

  // Ask for a review once the test has given the seller something: a sale, or a
  // finished test with visitors. Never while they're still waiting on it. The
  // server throttles repeats.
  const hasEarnedReview =
    (metrics?.totalPayments ?? 0) > 0 ||
    (experiment?.status === 'winner_declared' && (metrics?.totalVisits ?? 0) > 0);
  useEffect(() => {
    if (isReadOnly || !companyId || !hasEarnedReview || reviewChecked.current) return;
    reviewChecked.current = true;
    (async () => {
      try {
        const reviewStatus = await api.getReviewPromptStatus(companyId);
        if (reviewStatus.shouldShow) {
          await api.recordReviewPromptShown(companyId);
          setShowReviewPrompt(true);
        }
      } catch (reviewErr) {
        console.error('Failed to check review prompt status:', reviewErr);
      }
    })();
  }, [isReadOnly, companyId, hasEarnedReview]);

  async function runAction(action: () => Promise<Experiment>, failure: string) {
    setActionError(null);
    try {
      setExperiment(await action());
    } catch (err) {
      console.error(failure, err);
      setActionError(problemFrom(err, failure));
    }
  }

  async function handleGoLive() {
    if (!isTermsAccepted) {
      setShowTermsModal(true);
      return;
    }
    await performStart();
  }

  async function performStart() {
    setActionError(null);
    try {
      setExperiment(await api.startExperiment(id));
    } catch (err) {
      if (err instanceof PlanLimitError) {
        setShowUpgrade(true);
        return;
      }
      console.error('Failed to start test:', err);
      setActionError(problemFrom(err, "Couldn't start the test. Try again."));
      return;
    }
    loadAnalytics();
  }

  async function handleArchive() {
    setActionError(null);
    try {
      const result = await api.archiveExperiment(id);
      setExperiment(result.experiment);
      setShowArchiveConfirm(false);
    } catch (err) {
      console.error('Failed to archive test:', err);
      setActionError(problemFrom(err, "Couldn't archive the test. Try again."));
    }
  }

  const currency = metrics?.variants[0]?.currency || experiment?.variants?.[0]?.currency || 'usd';
  const control = metrics?.variants.find((v) => v.isControl) ?? metrics?.variants[0];

  // Variant cards for the selected spot filter.
  const views: VariantView[] = useMemo(() => {
    if (!metrics) return [];
    const priceOf = (v: VariantMetrics) => describeOffer(v);
    if (filter === 'all') {
      return metrics.variants.map((v) => ({
        variantId: v.variantId,
        name: variantLabel(v.variantName),
        price: priceOf(v),
        isControl: v.isControl,
        rpv: v.revenuePerVisitor,
        netRevenue: v.netRevenue,
        visitors: v.visits,
        // One "Bought" everywhere: unique starters (trials included) / visitors.
        boughtRate: startedPercent(v.starters, v.visits),
        refunds: v.refunds,
        cancellations: v.interval === 'one_time' ? null : v.cancellations,
        rpvVisitors: v.settled?.visits ?? v.visits,
        trialProgress: trialProgressLine(v),
        probability: v.probabilityToBeatControl,
        probabilityNote: null,
      }));
    }
    const row = breakdown.find((b) => (b.placementId ?? 'unattributed') === filter);
    return metrics.variants.map((v) => {
      const cell = row?.variants.find((x) => x.variantId === v.variantId);
      const visitors = cell?.visits ?? 0;
      return {
        variantId: v.variantId,
        name: variantLabel(v.variantName),
        price: priceOf(v),
        isControl: v.isControl,
        rpv: cell?.revenuePerVisitor ?? 0,
        netRevenue: cell?.netRevenue ?? 0,
        visitors,
        boughtRate: startedPercent(cell?.starters ?? 0, visitors),
        refunds: row ? (cell?.refunds ?? 0) : null,
        cancellations: null,
        rpvVisitors: visitors,
        trialProgress: null,
        probability: null,
        probabilityNote: 'spot',
      };
    });
  }, [metrics, breakdown, filter]);

  if (loadFailed && !experiment) {
    return (
      <Card size="3" style={{ maxWidth: 520, margin: 'var(--space-6) auto', textAlign: 'center' }}>
        <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
          Couldn't load this test
        </Heading>
        <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
          It may have been deleted, or the connection dropped.
        </Text>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 'var(--space-3)',
          }}
        >
          <Button size="2" variant="solid" onClick={loadExperiment}>
            Try again
          </Button>
          <Button size="2" variant="soft" color="gray" onClick={() => navigate('/')}>
            <ArrowLeft16 />
            Your tests
          </Button>
        </div>
      </Card>
    );
  }

  if (loading || !experiment) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
        <Spinner size="3" />
      </div>
    );
  }

  const offerClosed = activity?.offerClosed ?? null;
  const badge = statusBadge(experiment, offerClosed);
  const isLive = experiment.status === 'running';
  const isDraft = experiment.status === 'draft';
  const ended = experiment.status === 'winner_declared';
  const waiting = isLive && activity !== null && !activity.realTraffic && !offerClosed;
  const earlyRead = metrics?.earlyRead ?? true;
  const judgedByRevenue = metrics?.revenueComparable ?? true;
  // A trial test's money per spot and per day is cash so far, which favours the
  // shorter trial: only the whole-test cards are judged on revenue.
  const trialTest = Boolean(metrics?.trialTest);
  const word = offerWord(metrics);
  const trialNote = metrics ? trialTestNote(metrics) : null;
  const spotsByRevenue = judgedByRevenue && !trialTest;
  const totalVisitors = metrics?.totalVisits ?? 0;
  const variantCount = experiment.variants?.length || metrics?.variants.length || 2;
  // One source for the banner and the progress bar: the stats' earlyRead thresholds.
  const target = metrics ? sampleTarget(metrics, variantCount) : null;
  const unlock = experiment.offerType === 'unlock';
  const saleWord = saleWordFor(experiment.offerType, experiment.variants ?? []);
  const salesWord = `${saleWord}s`;
  const readout = metrics ? readTest(metrics, Boolean(offerClosed)) : null;
  const split = (experiment.variants ?? []).map((v) => experiment.split[v.name] ?? 0).join(' / ');
  const state: TestState = {
    status: experiment.status,
    archived: experiment.archived,
    offerType: experiment.offerType,
  };
  const linkSpots = placements.filter((p) => p.link);
  const spotCount = placements.length;
  const canAddSpot = !isReadOnly && !experiment.archived && !ended && !offerClosed;
  const winner = experiment.variants?.find((v) => v._id === experiment.winnerVariantId);
  const summary = ended && winner && metrics ? endSummary(metrics, winner._id) : null;
  const inWhopSpot = placements.some((p) => p.kind === 'in_whop');
  const boughtLabel = boughtLabelFor(experiment.offerType, experiment.variants ?? []);
  // Ending can't be undone: never on numbers that didn't load.
  const canPick =
    !isReadOnly && !experiment.archived && !metricsFailed && (isLive || experiment.status === 'stopped');
  // Going live needs somewhere for buyers to arrive and, for an unlock, its image.
  const setupUnfinished =
    isDraft && !spotsFailed && (spotCount === 0 || (unlock && !experiment.unlock?.contentKey));
  // An ended test's links are settled: they sell the winner.
  const manage: SpotManagement | undefined =
    isReadOnly || ended || experiment.archived
    ? undefined
    : {
        experimentId: id,
        companyId,
        spotCount,
        visitsFor: (placementId) =>
          breakdownLoaded
            ? (breakdown.find((b) => b.placementId === placementId)?.visits ?? 0)
            : null,
        onChanged: loadAnalytics,
      };
  const runAnotherTest = () =>
    navigate(
      unlock
        ? '/new'
        : `/experiments/new?path=${pathFromPlacements(placements) ?? 'link'}&product=${experiment.productId}`
    );
  const pricesLine = (experiment.variants ?? [])
    .map((v) => `${variantLabel(v.name)} ${describeOffer(v)}`)
    .join(' · ');
  // PlacementLinks lists an in-whop spot only when it can be managed.
  const refusedPartners = placements.filter((p) => p.affiliateRejected);
  const spotRows = linkSpots.length + (manage ? placements.filter((p) => p.kind === 'in_whop').length : 0);
  // A partner losing credit is never tucked away.
  const spotsFolded = spotRows > SPOTS_SHOWN_OPEN && !spotsOpen && refusedPartners.length === 0;
  const spotsCard = !isDraft && !waiting && spotRows > 0 && !isReadOnly && (
    <Rise>
      <Card size="2">
        <Heading size="3" style={{ marginBottom: 'var(--space-1)' }}>
          {linkSpots.length === 0
            ? 'Inside your whop'
            : linkSpots.length === 1
              ? 'Your link'
              : `Your links (${linkSpots.length})`}
        </Heading>
        {linkSpots.length > 0 && (
          <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
            {offerClosed
              ? `Anyone who opens ${linkSpots.length === 1 ? 'it' : 'them'} sees the offer as closed. Past buyers can still open what they bought.`
              : linksNow(state, linkSpots.length)}
          </Text>
        )}
        {spotsFolded ? (
          <Button type="button" variant="soft" color="gray" size="2" onClick={() => setSpotsOpen(true)}>
            Show all {spotRows}
          </Button>
        ) : (
          <PlacementLinks placements={placements} manage={manage} />
        )}
      </Card>
    </Rise>
  );
  // One button: End test until there's a result, then Pick winner.
  const resultReady = judgedByRevenue && !earlyRead;
  // Always say when a winner can be called, while it still can be.
  const whenResult =
    judgedByRevenue && earlyRead && target && !offerClosed
      ? `Pick a winner once each ${word} has ${formatNumber(target.perVariant)} visitors and ${formatNumber(target.conversions)} ${salesWord}, so you can trust the result.`
      : null;
  const shortfall = whenResult && metrics ? sampleShortfall(metrics, saleWord) : null;
  // The setup guide leads until there's real traffic; the target sits under the header.
  const showBanner = !ended && !waiting && metrics !== null && totalVisitors > 0;
  const liveLine = isLive
    ? liveFor(experiment.startedAt, 'Live')
    : ended && experiment.endedAt
      ? liveFor(experiment.startedAt, 'Ran', new Date(experiment.endedAt))
      : null;
  const filterRows = breakdown.filter((b) => b.visits > 0 || b.placementId !== null);
  const challengers = metrics?.variants.filter((v) => !v.isControl) ?? [];

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto' }}>
      {/* Header */}
      <Reveal>
        <div style={{ marginBottom: 'var(--space-5)' }}>
          <Button
            variant="ghost"
            color="gray"
            size="2"
            onClick={() => navigate('/')}
            style={{ marginBottom: 'var(--space-3)' }}
          >
            <ArrowLeft16 />
            Your tests
          </Button>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
            }}
          >
            <div style={{ minWidth: 0, flex: '1 1 280px' }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                <Heading size={isMobile ? '6' : '7'} style={{ overflowWrap: 'anywhere' }}>
                  {experiment.name}
                </Heading>
                <Badge size="2" variant="soft" color={badge.color}>
                  {badge.label}
                </Badge>
                {isReadOnly && (
                  <Badge size="2" variant="soft" color="orange">
                    Read only
                  </Badge>
                )}
              </div>
              <Text size="2" color="gray">
                {[
                  liveLine,
                  experiment.productName,
                  placements.length > 0 ? placesLabel(placements) : null,
                  split ? `split ${split}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
              {/* With no results yet, nothing else on the page says what's being tested. */}
              {totalVisitors === 0 && pricesLine && !metricsFailed && (
                <Text size="2" color="gray" className="vara2-tabular" style={{ display: 'block' }}>
                  {pricesLine}
                </Text>
              )}
              {isReadOnly && (
                <Text size="1" color="gray" style={{ display: 'block' }}>
                  Company: {experiment.companyId}
                </Text>
              )}
            </div>

            {!isReadOnly && !experiment.archived && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {isDraft && setupUnfinished && (
                  <Button asChild variant="solid" size="2">
                    <Link to={`/experiments/new?id=${id}`}>
                      {unlock && !experiment.unlock?.contentKey ? 'Open draft' : 'Finish setup'}
                    </Link>
                  </Button>
                )}
                {isDraft && !setupUnfinished && (
                  <Button variant="solid" color="green" size="2" onClick={handleGoLive}>
                    <Play16 />
                    Go live
                  </Button>
                )}
                {isLive && !offerClosed && (
                  <Button
                    variant="soft"
                    color="gray"
                    size="2"
                    onClick={() =>
                      runAction(
                        () => api.stopExperiment(id),
                        "Couldn't pause the test. Try again."
                      )
                    }
                  >
                    <Pause16 />
                    Pause
                  </Button>
                )}
                {experiment.status === 'stopped' && !offerClosed && (
                  <Button
                    variant="soft"
                    color="green"
                    size="2"
                    onClick={() =>
                      runAction(
                        () => api.resumeExperiment(id),
                        "Couldn't resume the test. Try again."
                      )
                    }
                  >
                    <Play16 />
                    Resume
                  </Button>
                )}
                {canPick &&
                  (resultReady ? (
                    <Button variant="solid" size="2" onClick={() => setPromoteMode('pick')}>
                      <Trophy16 />
                      Pick winner
                    </Button>
                  ) : (
                    // The modal warns on an early read (v2 design §3): their call.
                    <Button variant="soft" color="gray" size="2" onClick={() => setPromoteMode('early')}>
                      End test
                    </Button>
                  ))}
              </div>
            )}
          </div>
          {/* The banner says this once it's showing. */}
          {canPick && !showBanner && whenResult && (
            <Text size="1" color="gray" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
              {whenResult}
            </Text>
          )}
        </div>
      </Reveal>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {actionError && <ProblemCallout problem={actionError} />}
        {(metricsFailed || spotsFailed) && (
          <LoadFailed
            what={metricsFailed && spotsFailed ? "this test's results and links" : metricsFailed ? 'your results' : "this test's links"}
            onRetry={loadAnalytics}
          />
        )}
        {offerClosed && !isReadOnly && (
          <Callout.Root color="orange">
            <Callout.Icon>
              <ExclamationTriangle16 />
            </Callout.Icon>
            <Callout.Text>
              {OFFER_CLOSED[offerClosed].body}
              {!ended && !experiment.archived ? ` ${CLOSED_NEXT_STEP}` : ''}
            </Callout.Text>
          </Callout.Root>
        )}
        {refusedPartners.length > 0 && !isReadOnly && !ended && (
          <Callout.Root color="red">
            <Callout.Icon>
              <ExclamationTriangle16 />
            </Callout.Icon>
            <Callout.Text>
              Whop didn't accept the username for {refusedPartners.map(spotName).join(', ')}, so{' '}
              {refusedPartners.length === 1 ? 'that partner gets' : 'those partners get'} no credit for
              sales. Change it under Rename, in your links below.
            </Callout.Text>
          </Callout.Root>
        )}

        {/* Ended: the final read replaces the early-read banner */}
        {ended && winner && (
          <Rise>
            <Card
              size="2"
              style={{ backgroundColor: 'var(--blue-a3)', border: '1px solid var(--blue-a5)' }}
            >
              <Text size="1" color="gray" style={{ display: 'block' }}>
                Winner
              </Text>
              <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
                {variantLabel(winner.name)} · {describeOffer(winner)}
              </Heading>
              {summary?.lift != null && (
                <Text
                  size="3"
                  weight="medium"
                  className="vara2-tabular"
                  style={{ display: 'block', marginBottom: 'var(--space-1)' }}
                >
                  {formatLift(summary.lift)} revenue per visitor vs Control
                </Text>
              )}
              {!metricsFailed && (
                <Text size="2" color="gray" style={{ display: 'block' }}>
                  {summary?.decided ?? 'It ended before anyone visited, so there was nothing to compare.'}
                </Text>
              )}
              {inWhopSpot && !experiment.archived && (
                <Text
                  size="2"
                  color="gray"
                  style={{ display: 'block', marginTop: 'var(--space-1)' }}
                >
                  Free members inside your whop see the winner as their upgrade offer, until you
                  run another test on that free product.
                </Text>
              )}
              {(experiment.unarchivedLosers ?? []).length > 0 && (
                <Text size="2" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
                  Whop didn't let us archive{' '}
                  {experiment.unarchivedLosers!.map(variantLabel).join(' and ')}. Your links don't
                  send anyone to {experiment.unarchivedLosers!.length === 1 ? 'it' : 'them'}, and
                  you can archive {experiment.unarchivedLosers!.length === 1 ? 'it' : 'them'} from
                  your Whop dashboard.
                </Text>
              )}
              {experiment.endedAt && (
                <Text
                  size="1"
                  color="gray"
                  style={{ display: 'block', marginTop: 'var(--space-2)' }}
                >
                  {trialTest
                    ? "New sales after the test ended aren't counted here. Free trials that began before it ended count once they're over."
                    : "Sales after the test ended aren't counted here."}
                </Text>
              )}
              {!experiment.archived && !isReadOnly && (
                <Button
                  variant="solid"
                  size="3"
                  onClick={runAnotherTest}
                  style={{ marginTop: 'var(--space-3)' }}
                >
                  Run another test
                </Button>
              )}
            </Card>
          </Rise>
        )}

        {/* Draft: links don't work until live */}
        {isDraft && !experiment.archived && !isReadOnly && (
          <Rise>
            <Card
              size="2"
              style={{ backgroundColor: 'var(--accent-a3)', border: '1px solid var(--accent-a6)' }}
            >
              <Heading size="4" style={{ marginBottom: 'var(--space-1)' }}>
                This test isn't live yet
              </Heading>
              {needsProForNewTest(plan) && (
                <Text size="2" style={{ display: 'block', marginBottom: 'var(--space-1)' }}>
                  You've used your free test, so going live with this one needs Pro.
                </Text>
              )}
              <Text size="2" color="gray" style={{ display: 'block' }}>
                {unlock
                  ? 'Go live to start splitting buyers. Until then, its offer page shows the offer as unavailable and nothing is tracked.'
                  : 'Go live to start splitting buyers. Until then, anyone who opens its links lands on your regular Whop store and nothing is tracked.'}
              </Text>
              {linkSpots.length > 0 && (
                <Text
                  size="2"
                  color="gray"
                  style={{ display: 'block', marginTop: 'var(--space-2)' }}
                >
                  Spots ready: {linkSpots.map(spotName).join(', ')}
                </Text>
              )}
            </Card>
          </Rise>
        )}

        {/* Live without real traffic yet: teach where to put it */}
        {waiting && !isReadOnly && !spotsFailed && (
          <Rise>
            <Card size={isMobile ? '2' : '3'}>
              <FirstVisitChecklist experimentId={id} placements={placements} manage={manage} productName={experiment.productName} />
            </Card>
          </Rise>
        )}

        {/* With no results to show first, the spots lead. Otherwise they follow the cards. */}
        {totalVisitors === 0 && spotsCard}

        {metrics?.legacyHistory && (
          <Text size="2" color="gray">
            {describeLegacyHistory(metrics.legacyHistory, currency)}
          </Text>
        )}

        {metrics && totalVisitors > 0 && (
          <>
            {/* Early read / result banner (an ended test shows its final read above) */}
            {showBanner && (
              <Rise delay={80}>
                <Card
                  size="2"
                  style={{
                    backgroundColor: earlyRead ? 'var(--yellow-a2)' : 'var(--green-a2)',
                    border: `1px solid ${earlyRead ? 'var(--yellow-a5)' : 'var(--green-a5)'}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                    <InfoCircle16
                      style={{ flexShrink: 0, marginTop: 3, color: 'var(--gray-11)' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="3" weight="bold" style={{ display: 'block' }}>
                        {!judgedByRevenue
                          ? "We can't judge this comparison by revenue yet"
                          : earlyRead
                            ? offerClosed
                              ? 'Not enough data for a reliable answer'
                              : isLive
                                ? 'Early read — keep it running'
                                : 'Early read'
                            : (readout?.text ?? 'Result')}
                      </Text>
                      {filter !== 'all' && (
                        <Text size="1" color="gray" style={{ display: 'block' }}>
                          For the whole test
                        </Text>
                      )}
                      <Text size="2" color="gray" style={{ display: 'block', marginTop: 2 }}>
                        {!judgedByRevenue
                          ? `${NOT_JUDGED_BY_REVENUE} ${pluralize(totalVisitors, 'visitor')} so far.`
                          : earlyRead && offerClosed
                          ? `${pluralize(totalVisitors, 'visitor')} in all. This offer can't sell any more, so these are its final numbers.`
                          : whenResult
                          ? `${readout?.tone === 'early' && metrics.trialTest?.countedVisitors !== 0 ? `${readout.text}. ` : ''}${whenResult}`
                          : readout?.tone === 'close'
                            ? `Enough visitors and ${salesWord}, and neither ${word} clearly earns more.${offerClosed ? '' : ` Keep your current ${word}, or keep it running.`}`
                            : `Enough visitors and ${salesWord} for a result.`}
                      </Text>
                      {trialNote && (
                        <Text size="2" color="gray" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                          {trialNote}
                        </Text>
                      )}
                      {whenResult && target && (
                        <Progress
                          value={target.percent}
                          size="2"
                          aria-label="Progress to a result"
                          style={{ marginTop: 'var(--space-3)' }}
                        />
                      )}
                      {canPick && shortfall && (
                        <Text size="1" color="gray" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
                          {shortfall}
                        </Text>
                      )}
                    </div>
                  </div>
                </Card>
              </Rise>
            )}

            {/* Spot filter */}
            {filterRows.length > 1 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                }}
              >
                <Text size="2" color="gray">
                  Showing
                </Text>
                {[
                  { key: 'all', label: 'All sources' },
                  ...filterRows.map((b) => ({
                    key: b.placementId ?? 'unattributed',
                    label: b.placementId ? spotName(b) : NO_SPOT,
                  })),
                ].map((f) => (
                  <Button
                    key={f.key}
                    size="2"
                    variant={filter === f.key ? 'solid' : 'surface'}
                    color={filter === f.key ? undefined : 'gray'}
                    aria-pressed={filter === f.key}
                    title={f.label}
                    style={{ maxWidth: '100%' }}
                    onClick={() => setFilter(f.key)}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.label}
                    </span>
                  </Button>
                ))}
              </div>
            )}

            {/* Variant cards */}
            <Rise delay={140}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: 'var(--space-4)',
                }}
              >
                {views.map((view) => (
                  <VariantCard
                    key={view.variantId}
                    view={view}
                    currency={currency}
                    // An ended or archived test's numbers won't move.
                    earlyRead={earlyRead && !ended && !experiment.archived && !offerClosed}
                    boughtLabel={boughtLabel}
                    judgedByRevenue={judgedByRevenue && (!trialTest || filter === 'all')}
                    afterTrials={trialTest && filter === 'all'}
                  />
                ))}
              </div>
            </Rise>

            {metrics.otherCurrencySales > 0 && (
              <Text size="2" color="gray">
                {pluralize(metrics.otherCurrencySales, 'sale')} in another currency{' '}
                {metrics.otherCurrencySales === 1 ? "isn't" : "aren't"} in these numbers, so the
                totals stay in one currency.
              </Text>
            )}

            {spotsCard}

            {/* By spot: nothing to break down with one spot. */}
            {breakdown.length > 1 && control && (
              <Rise delay={200}>
                <Card size="2">
                  <Heading size="3" style={{ marginBottom: 'var(--space-1)' }}>
                    By source
                  </Heading>
                  <Text
                    size="2"
                    color="gray"
                    style={{ display: 'block', marginBottom: 'var(--space-3)' }}
                  >
                    {spotsByRevenue ? 'Revenue per visitor' : 'People who started'}, Control vs{' '}
                    {challengers.length > 1 ? 'Challengers' : 'Challenger'}
                  </Text>
                  <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                    <Table.Root variant="surface" style={{ minWidth: isMobile ? 420 : 480 }}>
                      <Table.Table>
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Visitors</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Control</Table.ColumnHeaderCell>
                            {challengers.map((c) => (
                              <Table.ColumnHeaderCell key={c.variantId}>
                                {variantLabel(c.variantName)}
                              </Table.ColumnHeaderCell>
                            ))}
                            {spotsByRevenue && <Table.ColumnHeaderCell>So far</Table.ColumnHeaderCell>}
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {breakdown.map((row) => {
                            const cellFor = (variantId: string) =>
                              row.variants.find((v) => v.variantId === variantId);
                            const controlRpv = cellFor(control.variantId)?.revenuePerVisitor ?? 0;
                            const visitsOf = (variantId: string) => cellFor(variantId)?.visits ?? 0;
                            const spotValue = (variantId: string) =>
                              spotsByRevenue
                                ? visitsOf(variantId) === 0
                                  ? '—'
                                  : formatRpv(cellFor(variantId)?.revenuePerVisitor ?? 0, currency)
                                : formatNumber(cellFor(variantId)?.starters ?? 0);
                            const bestChallengerRpv = Math.max(
                              0,
                              ...challengers.map(
                                (c) => cellFor(c.variantId)?.revenuePerVisitor ?? 0
                              )
                            );
                            return (
                              <Table.Row key={row.placementId ?? 'unattributed'}>
                                {/* A long name mustn't take the whole phone screen from the numbers. */}
                                <Table.RowHeaderCell
                                  style={
                                    isMobile
                                      ? { maxWidth: 110, whiteSpace: 'normal', overflowWrap: 'break-word' }
                                      : undefined
                                  }
                                >
                                  {row.placementId ? spotName(row) : NO_SPOT}
                                </Table.RowHeaderCell>
                                <Table.Cell className="vara2-tabular">
                                  {formatNumber(row.visits)}
                                </Table.Cell>
                                <Table.Cell className="vara2-tabular">
                                  {spotValue(control.variantId)}
                                </Table.Cell>
                                {challengers.map((c) => (
                                  <Table.Cell key={c.variantId} className="vara2-tabular">
                                    {spotValue(c.variantId)}
                                  </Table.Cell>
                                ))}
                                {spotsByRevenue && (
                                  <Table.Cell>
                                    {row.visits === 0 && row.netRevenue !== 0
                                      ? `${formatRpv(row.netRevenue, currency)} in sales, no visitors recorded`
                                      : readSpot(
                                          row.visits,
                                          controlRpv,
                                          bestChallengerRpv,
                                          metrics.minVisitorsPerVariant
                                        )}
                                  </Table.Cell>
                                )}
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Table>
                    </Table.Root>
                  </div>
                </Card>
              </Rise>
            )}

            <Rise delay={260}>
              <PerformanceTrend
                experimentId={id}
                companyId={companyId}
                since={experiment.startedAt ?? experiment.createdAt}
                until={experiment.endedAt ?? undefined}
                currency={currency}
                boughtLabel={boughtLabel}
                revenueComparable={spotsByRevenue}
              />
            </Rise>
          </>
        )}

        {canAddSpot && (
          <Card size="2">
            <Heading size="3" style={{ marginBottom: 'var(--space-1)' }}>
              More buyers, faster answer
            </Heading>
            <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
              Post this test somewhere else too. Results combine, and you can still see each
              source here.
            </Text>
            <Button asChild size="2" variant="soft">
              <Link to={`/experiments/${id}/spots/new`}>
                <Plus16 />
                Add a link
              </Link>
            </Button>
          </Card>
        )}
      </div>

      {/* Archive */}
      {!isReadOnly && (
        <div
          style={{
            marginTop: 'var(--space-6)',
            paddingTop: 'var(--space-5)',
            borderTop: '1px solid var(--gray-a6)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
          }}
        >
          <div style={{ flex: '1 1 260px' }}>
            <Text
              size="2"
              weight="medium"
              style={{ display: 'block', marginBottom: 'var(--space-1)' }}
            >
              {experiment.archived ? 'Archived test' : 'Archive test'}
            </Text>
            <Text size="2" color="gray">
              {experiment.archived
                ? unarchiveHint(state)
                : ended
                  ? 'Tucks it away under Ended. Its results are kept.'
                  : isLive
                    ? 'Stops the test without picking a winner and moves it to Ended. Its results are kept.'
                    : 'Moves it to Ended without picking a winner. Its results are kept.'}
            </Text>
          </div>
          {experiment.archived ? (
            <Button
              variant="soft"
              color="gray"
              size="2"
              onClick={() =>
                runAction(() => api.unarchiveExperiment(id), "Couldn't unarchive. Try again.")
              }
            >
              <Eye16 />
              Unarchive
            </Button>
          ) : (
            <Button
              variant="soft"
              color="gray"
              size="2"
              onClick={() =>
                isLive || placements.length > 0 ? setShowArchiveConfirm(true) : handleArchive()
              }
            >
              <EyeSlashed16 />
              Archive
            </Button>
          )}
        </div>
      )}

      {promoteMode && (
        <PromoteWinnerModal
          experiment={experiment}
          metrics={metrics}
          placements={placements}
          early={promoteMode === 'early'}
          offerClosed={offerClosed}
          onClose={() => setPromoteMode(null)}
          onSuccess={() => {
            setPromoteMode(null);
            loadExperiment();
          }}
        />
      )}

      {showArchiveConfirm && (
        <Dialog.Root open onOpenChange={(open: boolean) => !open && setShowArchiveConfirm(false)}>
          <Dialog.Content style={{ maxWidth: 480 }}>
            <Dialog.Title size="4" style={{ marginBottom: 'var(--space-2)' }}>
              {isLive ? 'Stop and archive this live test?' : 'Archive this test?'}
            </Dialog.Title>
            <Dialog.Description
              size="2"
              color="gray"
              style={{ display: 'block', marginBottom: 'var(--space-4)' }}
            >
              {archiveEffect(placements, state)}
            </Dialog.Description>
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <Button
                variant="soft"
                color="gray"
                size="2"
                onClick={() => setShowArchiveConfirm(false)}
              >
                Cancel
              </Button>
              <Button variant="solid" color="red" size="2" onClick={handleArchive}>
                {isLive ? 'Stop & archive' : 'Archive'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Root>
      )}

      {showTermsModal && (
        <TermsAcceptanceModal
          onCancel={() => setShowTermsModal(false)}
          onAccepted={() => {
            setShowTermsModal(false);
            performStart();
          }}
        />
      )}

      {showReviewPrompt && (
        <ReviewPromptModal companyId={companyId} onClose={() => setShowReviewPrompt(false)} />
      )}

      {showUpgrade && (
        <UpgradeDialog
          companyId={companyId}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={() => {
            setShowUpgrade(false);
            performStart();
          }}
        />
      )}
    </div>
  );
}
